import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Expander, Text } from '~/components/core';
import Checkbox from '~/components/core/Checkbox';
import { useStyles } from '~/hooks';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { CreateStrategyWizardApi } from '../../../hooks/useCreateStrategyWizard';
import NodeAttributeForm from '../NodeAttributeForm';

type Props = {
    wizard: CreateStrategyWizardApi;
};

// Step 4: the GA fitness goal + optional entry filters supported by the chosen instrument.
export default function StepOptimisation({ wizard }: Props) {
    const styles = useStyles(stylesGenerator);

    return (
        <View>
            <Text style={styles.fieldLabel}>ai-strategy-optimization-goal</Text>
            {wizard.optimizationTypes.map((o) => {
                const selected = wizard.optimizationType === o.value;
                return (
                    <Pressable
                        key={o.value}
                        style={[styles.radioRow, selected ? styles.radioRowSelected : null]}
                        onPress={() => wizard.setOptimizationType(o.value)}
                    >
                        <View style={[styles.radioOuter, selected ? styles.radioOuterSelected : null]}>
                            {selected ? <View style={styles.radioInner} /> : null}
                        </View>
                        <Text noTranslate style={styles.radioLabel}>
                            {o.text}
                        </Text>
                    </Pressable>
                );
            })}

            <Text style={styles.fieldLabel}>ai-strategy-entry-filters</Text>
            {wizard.supportedOptimisationCount === 0 ? (
                <Text style={styles.emptyText}>ai-strategy-no-optimisations</Text>
            ) : (
                wizard.sideOptimisations.map((opt, i) => {
                    // Skip in place (don't filter the list) so `i` stays aligned with
                    // optimisationStates and the payload builder.
                    if (!wizard.optimisationSupportsInstrument(opt)) {
                        return null;
                    }
                    const state = wizard.optimisationStates[i];
                    const selected = !!state;
                    return (
                        <View key={opt.code} style={styles.optGroup}>
                            <Pressable style={styles.optRow} onPress={() => wizard.toggleOptimisation(i)}>
                                <Checkbox checked={selected} setChecked={() => wizard.toggleOptimisation(i)} />
                                <Text noTranslate style={styles.optLabel}>
                                    {opt.name}
                                </Text>
                            </Pressable>
                            {selected ? (
                                <>
                                    {opt.connections.map((conn, ci) => (
                                        <Text noTranslate key={ci} style={styles.conditionText}>
                                            {wizard.describeConnection(conn)}
                                        </Text>
                                    ))}
                                    {opt.indicatorInstances.map((code) => {
                                        const node = state.nodes[code];
                                        const canEdit = !!node && wizard.paramFields(code).some((f) => !f.hidden);
                                        if (!canEdit) {
                                            return null;
                                        }
                                        return (
                                            <Expander key={code} title={wizard.instanceLabel(code)}>
                                                <NodeAttributeForm
                                                    attrs={wizard.paramFields(code)}
                                                    value={node}
                                                    onChange={(v) =>
                                                        wizard.updateOptimisation(i, {
                                                            nodes: { ...state.nodes, [code]: v },
                                                        })
                                                    }
                                                />
                                            </Expander>
                                        );
                                    })}
                                </>
                            ) : null}
                        </View>
                    );
                })
            )}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        fieldLabel: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginTop: scaleSize(14),
            marginBottom: scaleSize(6),
        },
        radioRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: scaleSize(8),
            paddingHorizontal: scaleSize(10),
            borderRadius: scaleSize(8),
            borderWidth: 1,
            borderColor: theme.equityValue,
            marginBottom: scaleSize(6),
        },
        radioRowSelected: {
            borderColor: theme.button,
        },
        radioOuter: {
            width: scaleSize(16),
            height: scaleSize(16),
            borderRadius: scaleSize(8),
            borderWidth: 1.5,
            borderColor: theme.tabIconDefault,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: scaleSize(10),
        },
        radioOuterSelected: {
            borderColor: theme.button,
        },
        radioInner: {
            width: scaleSize(8),
            height: scaleSize(8),
            borderRadius: scaleSize(4),
            backgroundColor: theme.button,
        },
        radioLabel: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        emptyText: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            fontStyle: 'italic',
        },
        optGroup: {
            marginBottom: scaleSize(8),
        },
        optRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: scaleSize(6),
        },
        optLabel: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginLeft: scaleSize(10),
        },
        conditionText: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginLeft: scaleSize(30),
            marginBottom: scaleSize(4),
        },
    });
